import React, { useState, useEffect } from "react";
import { Card, IconButton, Icon, Tabs, Tab, Grid, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { get, postNoResponse } from "utils/WebService";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import MDSelect from "components/MDSelect";
import SeletorMarcacoes from "./SeletorMarcacoes";
import NovaFichaHonorariosDialog from "./NovaFichaHonorariosDialog";
import MDSnackbar from "components/MDSnackbar";
import MDButton from "components/MDButton";
import { TIPO_PERFIL, temTipoPerfil } from "utils/Perfis";
import Contratos from "./Contratos";
import Historico from "./Historico";
import Acordos from "./Acordos";
import { ESCURO } from "assets/coresTema";
import { ehCarteiraDeCobranca } from "utils/Carteiras";

export default function Honorarios({ formRef, recordId, isViewOnly, pendingContratos = [], setPendingContratos, changeTab, onDataUpdated, refreshKey, preloadedDevedor, preloadedContratos, preloadedAcordos, preloadedHistoricos, onNavigateToDevedor }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [fichas, setFichas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFichaId, setActiveFichaId] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };
    const { userPerfils, userFunctionalities } = useUser();
    
    // Roles & Permissões
    const canCreate = userFunctionalities && userFunctionalities.includes("c050_C");
    const canEdit = userFunctionalities && userFunctionalities.includes("c050_U");
    const isAdminOrGestor = temTipoPerfil(userPerfils, TIPO_PERFIL.ADMINISTRADOR, TIPO_PERFIL.SUPORTE, TIPO_PERFIL.GESTOR);
    const isSupervisorOrCoordenador = temTipoPerfil(userPerfils, TIPO_PERFIL.SUPERVISOR, TIPO_PERFIL.COORDENADOR);
    const isOperador = !canCreate && !canEdit && !isAdminOrGestor && !isSupervisorOrCoordenador;
    const podeNovaFicha = canCreate && !isViewOnly && !!recordId;

    // Form States
    const [editForm, setEditForm] = useState({ carteiraId: "", statusId: "", usuarioId: "", marcacoesIds: [] });
    const [carteirasOptions, setCarteirasOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [usuariosOptions, setUsuariosOptions] = useState([]);
    const [marcacoesOptions, setMarcacoesOptions] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    
    // Nova ficha de honorários: a mesma janela da aba Contratos
    const [novaFichaAberta, setNovaFichaAberta] = useState(false);

    // Snackbar States
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const loadFichas = () => {
        if (preloadedDevedor !== undefined) {
            if (preloadedDevedor && preloadedDevedor.fichas) {
                const honorarios = preloadedDevedor.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                setFichas(honorarios);
            } else {
                setFichas([]);
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        get(`/devedor/by-id/${recordId}`)
            .then(res => {
                if (res && res.fichas) {
                    const honorarios = res.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                    setFichas(honorarios);
                }
            })
            .catch(err => console.error("Erro ao carregar fichas de honorários", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (recordId || preloadedDevedor) {
            loadFichas();
        }
    }, [recordId, refreshKey, preloadedDevedor]);

    const activeFicha = activeFichaId ? fichas.find(f => f.id === activeFichaId || f.rawId === activeFichaId) : null;

    useEffect(() => {
        if (activeFicha) {
            setEditForm({
                carteiraId: activeFicha.idCarteira || activeFicha.carteiraId || "",
                statusId: activeFicha.idStatusFicha || activeFicha.statusId || "",
                usuarioId: activeFicha.idUsuarioDono || activeFicha.usuarioId || "",
                marcacoesIds: activeFicha.marcacoesIds || (activeFicha.tbMarcacoesEspeciais ? activeFicha.tbMarcacoesEspeciais.map(m => m.cvId || m.id) : [])
            });
            
            if (carteirasOptions.length === 0) {
                get("/carteira/usuario-contexto").then(res => {
                    let list = res?.carteiras || [];
                    // Sem a carteira de processos (pelo tipo da carteira, não pelo nome)
                    setCarteirasOptions(list.filter(ehCarteiraDeCobranca));
                }).catch(() => {
                    get("/carteira/list-all").then(res => {
                        let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                        setCarteirasOptions(list.filter(ehCarteiraDeCobranca));
                    }).catch(() => {});
                });
            }
            if (statusOptions.length === 0) {
                get("/status-ficha/list-all").then(res => {
                    let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                    setStatusOptions(list);
                }).catch(() => {});
            }
            if (usuariosOptions.length === 0) {
                get("/users/list-all-operators").then(res => setUsuariosOptions(res?.list || res?.data || (Array.isArray(res) ? res : []))).catch(() => {});
            }
            if (marcacoesOptions.length === 0) {
                get("/marcacoes").then(res => {
                    let list = res?.list || res?.data || (Array.isArray(res) ? res : []);
                    setMarcacoesOptions(list.filter(m => m.cvAlvo === 1 || m.cvAlvo === 3));
                }).catch(() => {});
            }
        }
    }, [activeFichaId, fichas]);

    const handleBackToGrid = () => {
        setActiveFichaId(null);
        setTabValue(0);
    };

    const handleOpenDetail = (ficha) => {
        setActiveFichaId(ficha.id || ficha.rawId);
        setTabValue(0);
    };

    const handleSaveEdit = () => {
        if (!activeFicha) return;
        setIsSaving(true);
        postNoResponse(`/devedor/carteira/update/${activeFicha.id || activeFicha.rawId}`, editForm)
            .then(() => {
                setSbMessage("Ficha de honorários atualizada com sucesso!");
                setSuccessSB(true);
                if (recordId) {
                    get(`/devedor/by-id/${recordId}`).then(res => {
                        if (res && res.fichas) {
                            const honorarios = res.fichas.filter(f => f.isHonorario === true || f.cbIsHonorario === true);
                            setFichas(honorarios);
                        }
                    }).catch(() => loadFichas());
                } else {
                    loadFichas();
                }
                if (onDataUpdated) onDataUpdated();
            })
            .catch(() => {
                setSbMessage("Erro ao atualizar ficha de honorários.");
                setErrorSB(true);
            })
            .finally(() => setIsSaving(false));
    };

    const handleOpenAdd = () => setNovaFichaAberta(true);

    const handleNovaFichaSalva = (res) => {
        setNovaFichaAberta(false);
        setSbMessage(res?.isTerceiro ? "Ficha de honorários do terceiro cadastrada." : `Ficha de honorários #${res?.numeroFicha} cadastrada.`);
        setSuccessSB(true);
        if (onDataUpdated) onDataUpdated();
        if (res?.isTerceiro && res?.idDevedor && onNavigateToDevedor) {
            onNavigateToDevedor(res.idDevedor);
        } else {
            loadFichas();
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("Deseja realmente excluir esta ficha de honorários? Todos os contratos atrelados a ela também serão apagados. O acordo formalizado ficará sem os honorários.")) {
            postNoResponse(`/devedor/carteira/delete/${id}`, {})
                .then(() => {
                    setSbMessage("Ficha excluída com sucesso!");
                    setSuccessSB(true);
                    if (onDataUpdated) onDataUpdated();
                    else loadFichas();
                })
                .catch(() => {
                    setSbMessage("Erro ao excluir ficha.");
                    setErrorSB(true);
                });
        }
    };

    const columns = [
        { 
            header: "ID", 
            accessorKey: "rawId", 
            align: "center", 
            width: "10%" 
        },
        { 
            header: "Carteira", 
            accessorKey: "nomeCarteira", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeCarteira || "Não informada"}
                </MDTypography>
            )
        },
        { 
            header: "Status", 
            accessorKey: "nomeStatusFicha", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeStatusFicha || "Sem status"}
                </MDTypography>
            )
        },
        { 
            header: "Propriedade", 
            accessorKey: "nomeUsuarioDono", 
            align: "left",
            cell: ({ row }) => (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                    {row.original.nomeUsuarioDono || "Sem responsável"}
                </MDTypography>
            )
        },
        {
            header: "Ações",
            accessorKey: "actions",
            align: "center",
            cell: ({ row }) => (
                <MDBox display="flex" justifyContent="center">
                    <IconButton onClick={() => handleOpenDetail(row.original)} color="info" size="small" title="Visualizar">
                        <Icon>visibility</Icon>
                    </IconButton>
                    <IconButton onClick={() => handleOpenDetail(row.original)} color="warning" size="small" title="Editar">
                        <Icon>edit</Icon>
                    </IconButton>
                    {(!isOperador && !isViewOnly) && (
                        <IconButton onClick={() => handleDelete(row.original.id)} color="error" size="small" title="Excluir">
                            <Icon>delete</Icon>
                        </IconButton>
                    )}
                </MDBox>
            )
        }
    ];

    return (
        <MDBox mt={2}>
            {activeFichaId ? (
                <Card id="honorarios-ficha-detalhe" sx={{ boxShadow: "none" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2} flexWrap="wrap" gap={1}>
                        <MDButton 
                            variant="outlined" 
                            color="secondary" 
                            size="small"
                            onClick={handleBackToGrid}
                            sx={{
                                color: darkMode ? "#ffffff !important" : undefined,
                                borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                            }}
                        >
                            <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> Voltar
                        </MDButton>

                        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                            {podeNovaFicha && (
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    size="small" 
                                    onClick={handleOpenAdd}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important",
                                        fontWeight: "bold",
                                        boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                        "&:hover": {
                                            backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                        }
                                    }}
                                >
                                    <Icon sx={{ mr: 0.5 }}>add</Icon> Nova Ficha de Honorários
                                </MDButton>
                            )}
                        </MDBox>
                    </MDBox>
                    
                    <MDBox sx={{ width: '100%', px: 2, mb: 4 }}>
                        <MDBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={handleChangeTab} aria-label="honorarios-tabs">
                                <Tab label="Dados" value={0} sx={{ fontSize: 12 }} />
                                <Tab label="Contratos" value={1} sx={{ fontSize: 12 }} />
                                <Tab label="Histórico" value={2} sx={{ fontSize: 12 }} />
                                <Tab label="Acordos" value={3} sx={{ fontSize: 12 }} />
                            </Tabs>
                        </MDBox>

                        <MDBox mt={3}>
                            <div role="tabpanel" hidden={tabValue !== 0}>
                                {tabValue === 0 && activeFicha && (
                                    <Card sx={{ p: 2, boxShadow: "none", bgcolor: darkMode ? ESCURO.cartao : "#fafafa", borderRadius: "12px", border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0"}` }}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>
                                                Dados da Ficha de Honorários #{activeFicha.rawId || activeFicha.id}
                                            </MDTypography>
                                        </MDBox>

                                        <Grid container spacing={3} alignItems="flex-start">
                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Carteira
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome}
                                                        placeholder="Selecione..."
                                                        value={carteirasOptions.find(c => String(c.id) === String(editForm.carteiraId)) || (activeFicha.nomeCarteira ? { id: activeFicha.idCarteira, nome: activeFicha.nomeCarteira } : null)}
                                                        options={carteirasOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, carteiraId: val ? val.id : "" })}
                                                        isDisabled={!isAdminOrGestor}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Status da Ficha
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome || option.ccNome}
                                                        placeholder="Selecione..."
                                                        value={statusOptions.find(s => String(s.id) === String(editForm.statusId)) || (activeFicha.nomeStatusFicha ? { id: activeFicha.idStatusFicha, nome: activeFicha.nomeStatusFicha } : null)}
                                                        options={statusOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, statusId: val ? val.id : "" })}
                                                        isDisabled={false}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Responsável (Propriedade)
                                                    </MDTypography>
                                                    <MDSelect
                                                        isMulti={false}
                                                        getOptionValue={(option) => option.id}
                                                        getOptionLabel={(option) => option.nome}
                                                        placeholder="Selecione..."
                                                        value={usuariosOptions.find(u => String(u.id) === String(editForm.usuarioId)) || (activeFicha.nomeUsuarioDono ? { id: activeFicha.idUsuarioDono, nome: activeFicha.nomeUsuarioDono } : null)}
                                                        options={usuariosOptions}
                                                        onChange={(val) => setEditForm({ ...editForm, usuarioId: val ? val.id : "" })}
                                                        isDisabled={isOperador || isViewOnly}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <MDBox>
                                                    <MDTypography 
                                                        variant="caption" 
                                                        fontWeight="bold" 
                                                        color={darkMode ? "white" : "text"} 
                                                        sx={{ display: "flex", alignItems: "center", minHeight: "24px", mb: 1 }}
                                                    >
                                                        Marcação Especial
                                                    </MDTypography>
                                                    <SeletorMarcacoes
                                                        carteira={editForm.carteiraId}
                                                        alvos={[1, 3]}
                                                        placeholder="Selecione..."
                                                        value={editForm.marcacoesIds}
                                                        onChange={(ids) => setEditForm({ ...editForm, marcacoesIds: ids })}
                                                        isDisabled={isOperador || isViewOnly}
                                                    />
                                                </MDBox>
                                            </Grid>

                                            <Grid item xs={12} display="flex" justifyContent="flex-end" mt={3} pt={2} sx={{ borderTop: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e0e0e0" }}>
                                                <MDButton 
                                                    variant="contained" 
                                                    color="info" 
                                                    onClick={handleSaveEdit}
                                                    disabled={isSaving}
                                                    sx={{
                                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                                        color: "#ffffff !important",
                                                        fontWeight: "bold",
                                                        boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                                        "&:hover": {
                                                            backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                                        }
                                                    }}
                                                >
                                                    {isSaving ? <CircularProgress size={18} color="inherit" /> : "Salvar Dados"}
                                                </MDButton>
                                            </Grid>
                                        </Grid>
                                    </Card>
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 1}>
                                {tabValue === 1 && (
                                    <Contratos 
                                        formRef={formRef} 
                                        recordId={recordId} 
                                        idDevedorCarteira={activeFichaId}
                                        isViewOnly={isViewOnly} 
                                        pendingContratos={pendingContratos} 
                                        setPendingContratos={setPendingContratos} 
                                        changeTab={handleChangeTab} 
                                        onDataUpdated={onDataUpdated} 
                                        isHonorariosTab={true}
                                        preloadedContratos={preloadedContratos}
                                        preloadedAcordos={preloadedAcordos}
                                        preloadedDevedor={preloadedDevedor}
                                    />
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 2}>
                                {tabValue === 2 && (
                                    <Historico 
                                        recordId={recordId} 
                                        isViewOnly={isViewOnly} 
                                        idDevedorCarteira={activeFichaId} 
                                        preloadedDevedor={preloadedDevedor}
                                        preloadedHistoricos={preloadedHistoricos}
                                        isHonorariosTab={true}
                                    />
                                )}
                            </div>
                            <div role="tabpanel" hidden={tabValue !== 3}>
                                {tabValue === 3 && (
                                    <Acordos 
                                        recordId={recordId} 
                                        isViewOnly={isViewOnly} 
                                        idDevedorCarteira={activeFichaId} 
                                        changeTab={handleChangeTab} 
                                        isHonorariosTab={true} 
                                        onDataUpdated={onDataUpdated} 
                                        preloadedAcordos={preloadedAcordos}
                                    />
                                )}
                            </div>
                        </MDBox>
                    </MDBox>
                </Card>
            ) : (
                <>
                    <MDBox display="flex" justifyContent="flex-end" mb={2}>
                        {podeNovaFicha && (
                            <MDButton 
                                variant="contained" 
                                color="info" 
                                size="small" 
                                onClick={handleOpenAdd}
                                sx={{
                                    backgroundColor: darkMode ? "#007bff !important" : undefined,
                                    color: "#ffffff !important",
                                    fontWeight: "bold",
                                    boxShadow: darkMode ? "0 4px 10px rgba(0, 123, 255, 0.3)" : undefined,
                                    "&:hover": {
                                        backgroundColor: darkMode ? "#0069d9 !important" : undefined
                                    }
                                }}
                            >
                                <Icon sx={{ mr: 0.5 }}>add</Icon> Nova Ficha de Honorários
                            </MDButton>
                        )}
                    </MDBox>

                    <DataTable
                        columnData={columns}
                        data={fichas}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                    />

                    {/* Fichas de Terceiros Vinculadas (Embargos de Terceiro) */}
                    {preloadedDevedor?.devedoresFilhos && preloadedDevedor.devedoresFilhos.length > 0 && (
                        <MDBox mt={4} mb={2}>
                            <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
                                <Icon sx={{ color: "#ffa000", fontSize: "22px !important" }}>account_tree</Icon>
                                <MDTypography variant="subtitle2" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    Honorários Cobrados de Terceiros Vinculados (Embargos de Terceiro)
                                </MDTypography>
                            </MDBox>
                            <Card sx={{ p: 2, backgroundColor: darkMode ? ESCURO.painel : "#fff8e1", border: `1px solid ${darkMode ? "#ffa000" : "#ffe082"}` }}>
                                <DataTable
                                    columnData={[
                                        {
                                            header: "Ficha",
                                            accessorKey: "unencryptedId",
                                            align: "center",
                                            width: "10%",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" fontWeight="bold" color="text">
                                                    #{row.original.unencryptedId || row.original.id}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Nome do Terceiro",
                                            accessorKey: "nome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDBox>
                                                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                                        {row.original.nome}
                                                    </MDTypography>
                                                    <MDTypography variant="caption" display="block" color="text" sx={{ fontSize: "11px" }}>
                                                        CPF/CNPJ: {row.original.documento || "Não informado"}
                                                    </MDTypography>
                                                </MDBox>
                                            )
                                        },
                                        {
                                            header: "Carteira",
                                            accessorKey: "carteiraNome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.carteiraNome || "Honorários"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Status",
                                            accessorKey: "statusFichaNome",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.statusFichaNome || "Sem status"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Responsável",
                                            accessorKey: "usuarioResponsavel",
                                            align: "left",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                                    {row.original.usuarioResponsavel || "Sem responsável"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Valor Original",
                                            accessorKey: "valorTotal",
                                            align: "right",
                                            cell: ({ row }) => (
                                                <MDTypography variant="caption" fontWeight="bold" color="text">
                                                    {row.original.valorTotal != null ? `R$ ${Number(row.original.valorTotal).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "R$ 0,00"}
                                                </MDTypography>
                                            )
                                        },
                                        {
                                            header: "Ação",
                                            accessorKey: "actions",
                                            align: "center",
                                            cell: ({ row }) => (
                                                <MDButton
                                                    variant="gradient"
                                                    color="warning"
                                                    size="small"
                                                    onClick={() => onNavigateToDevedor && onNavigateToDevedor(row.original.id)}
                                                    sx={{ py: 0.5, px: 1.5, fontSize: "11px", fontWeight: "bold" }}
                                                >
                                                    <Icon sx={{ mr: 0.5, fontSize: "16px !important" }}>open_in_new</Icon> Acessar Ficha
                                                </MDButton>
                                            )
                                        }
                                    ]}
                                    data={preloadedDevedor.devedoresFilhos}
                                    isSorted={false}
                                    entriesPerPage={false}
                                    showTotalEntries={false}
                                    noEndBorder
                                />
                            </Card>
                        </MDBox>
                    )}
                </>
            )}

            <NovaFichaHonorariosDialog
                open={novaFichaAberta}
                onClose={() => setNovaFichaAberta(false)}
                recordId={recordId}
                carteiraInicialNome={activeFicha?.nomeCarteira}
                onSalvo={handleNovaFichaSalva}
            />

            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                dateTime=""
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                dateTime=""
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite
            />
        </MDBox>
    );
}
